import React, { useState, useRef, useCallback, useMemo } from 'react';
import { Menu } from '../../components/Menu';
import type { MenuItem } from '../../components/Menu/types';

interface ViewerToolbarOverflowMenuProps {
  /** Menu items to display. When empty, the component renders nothing. */
  items: MenuItem[];
}

/**
 * Wrap item onClick handlers so the menu auto-closes for action items.
 * Checkbox items stay open so the user can toggle multiple settings.
 */
function withAutoClose(items: MenuItem[], close: () => void): MenuItem[] {
  return items.map((item) => {
    if (item.type === 'action') {
      const orig = item.onClick;
      return { ...item, onClick: () => { orig(); close(); } };
    }
    return item;
  });
}

/**
 * Generic three-dot overflow menu for viewer toolbars (document, text).
 *
 * Renders a vertical-ellipsis button styled as a `kollab-player-btn` and
 * opens a `Menu` dropdown above the toolbar. The caller is responsible for
 * building the items list; this component is viewer-agnostic.
 *
 * Action items auto-close the menu on click. Checkbox items stay open so
 * the user can toggle multiple settings without reopening the menu.
 *
 * Visibility is controlled by CSS container queries on the wrapper element
 * (the caller should add a class like `kollab-dv-overflow-btn` and a
 * corresponding `@container` rule).
 */
export const ViewerToolbarOverflowMenu = React.memo(
  function ViewerToolbarOverflowMenu({ items }: ViewerToolbarOverflowMenuProps) {
    const [isOpen, setIsOpen] = useState(false);
    const btnRef = useRef<HTMLButtonElement>(null);

    const close = useCallback(() => setIsOpen(false), []);
    const wrappedItems = useMemo(() => withAutoClose(items, close), [items, close]);

    if (items.length === 0) return null;

    return (
      <div style={{ position: 'relative' }}>
        <button
          ref={btnRef}
          className="kollab-player-btn"
          onClick={() => setIsOpen((v) => !v)}
          aria-label="More options"
          aria-haspopup="menu"
          aria-expanded={isOpen}
          data-tooltip="More"
          type="button"
        >
          <span className="kollab-viewer-overflow-dots">&#x22EE;</span>
        </button>
        <Menu
          items={wrappedItems}
          isOpen={isOpen}
          onClose={close}
          ariaLabel="More options"
          triggerRef={btnRef}
          className="kollab-toolbar-overflow-menu"
        />
      </div>
    );
  },
);
